import { useState, useEffect, useCallback } from 'react';
import { Plus, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@foundation/altzero_base/base/components/ui/button';
import { Card } from '@foundation/altzero_base/base/components/ui/card';
import { VenueManagement } from '../components/VenueManagement';
import { VenueForm } from '../components/VenueForm';
import { QRCodeDialog } from '../components/QRCodeDialog';
import { listVenues, createVenue, updateVenue, deleteVenue } from '../services/venueService';
import type { Venue, CreateVenuePayload, UpdateVenuePayload } from '../types/venue.types';
import { theme } from '../../../theme';

export const VenueManagementPage = () => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [qrVenue, setQrVenue] = useState<Venue | null>(null);

  const loadVenues = useCallback(async () => {
    try {
      const data = await listVenues();
      setVenues(data);
    } catch (error) {
      console.error('Failed to load venues:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  const handleCreate = async (payload: CreateVenuePayload) => {
    await createVenue(payload);
    await loadVenues();
  };

  const handleEdit = (venue: Venue) => {
    setEditingVenue(venue);
    setShowForm(true);
  };

  const handleEditSubmit = async (payload: CreateVenuePayload) => {
    if (!editingVenue) return;
    await updateVenue(editingVenue.id, payload as UpdateVenuePayload);
    setEditingVenue(null);
    await loadVenues();
  };

  const handleToggleActive = async (venue: Venue) => {
    if (venue.is_active) {
      await deleteVenue(venue.id);
    } else {
      await updateVenue(venue.id, { is_active: true });
    }
    await loadVenues();
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingVenue(null);
  };

  const activeCount = venues.filter((v) => v.is_active).length;
  const inactiveCount = venues.filter((v) => !v.is_active).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className={`w-8 h-8 ${theme.text.accent} ${theme.anim.spin}`} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${theme.text.heading} flex items-center gap-2`}>
            <MapPin className={`w-6 h-6 ${theme.text.accent}`} />
            Venue Management
          </h1>
          <p className={`text-sm ${theme.text.body} mt-1`}>
            Manage venues and generate QR codes for instant access
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Venue
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <p className={`text-sm ${theme.text.body}`}>Total Venues</p>
          <p className={`text-2xl font-bold ${theme.text.heading}`}>{venues.length}</p>
        </Card>
        <Card className="p-4">
          <p className={`text-sm ${theme.text.body}`}>Active</p>
          <p className={`text-2xl font-bold ${theme.text.accent}`}>{activeCount}</p>
        </Card>
        <Card className="p-4">
          <p className={`text-sm ${theme.text.body}`}>Inactive</p>
          <p className={`text-2xl font-bold ${theme.text.muted}`}>{inactiveCount}</p>
        </Card>
      </div>

      {/* Venue Table */}
      <VenueManagement
        venues={venues}
        onEdit={handleEdit}
        onQRCode={setQrVenue}
        onToggleActive={handleToggleActive}
      />

      {/* Create/Edit Form Dialog */}
      {showForm && (
        <VenueForm
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={editingVenue ? handleEditSubmit : handleCreate}
          venue={editingVenue}
        />
      )}

      {/* QR Code Dialog */}
      {qrVenue && (
        <QRCodeDialog
          open={!!qrVenue}
          onClose={() => setQrVenue(null)}
          venue={qrVenue}
        />
      )}
    </div>
  );
};
